'use client';

import Link from 'next/link';
import { resolveHref } from './navigation.utils';
import { useLocale } from '@/components/providers';
import type { CtaItemDto, NavItemDto } from '@/lib/sanity/dto';
import type { ReactNode } from 'react';

/**
 * Every internal link needs the locale for its path prefix. Reading it from
 * context rather than taking it as a prop keeps the whole page-builder
 * (`UiButton`, `Hero`, `Cta`, every block) free of locale plumbing — they hand
 * over a nav item and nothing else, exactly as before.
 */
type BaseLinkProps = {
    item: NavItemDto | CtaItemDto | null;
    className?: string;
    children?: ReactNode;
    ariaCurrent?: 'page' | 'true';
    scroll?: boolean;
    ariaHaspopup?: 'dialog';
    queryParams?: Record<string, string>;
    onClick?: () => void;
};

export function BaseLink({
    item,
    className,
    children,
    ariaCurrent,
    scroll,
    ariaHaspopup,
    queryParams,
    onClick,
}: BaseLinkProps) {
    const locale = useLocale();

    if (!item) return null;

    const href = resolveHref(item, locale, queryParams);
    const isExternal = item.linkType === 'external';

    if (isExternal) {
        return (
            <a
                href={href}
                className={className}
                target="_blank"
                rel="noopener noreferrer"
                aria-current={ariaCurrent}
                onClick={onClick}
            >
                {children ?? item.label}
            </a>
        );
    }

    return (
        <Link
            href={href}
            className={className}
            aria-current={ariaCurrent}
            aria-haspopup={ariaHaspopup}
            scroll={scroll}
            onClick={onClick}
        >
            {children ?? item.label}
        </Link>
    );
}
